import { Badge, Button } from '@auctisync/ui';
import { formatCents, type Tables, type TablesInsert } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';

type Item = Tables<'inventory_items'>;
type Category = Pick<Tables<'item_categories'>, 'id' | 'name'>;
type Consignor = Pick<Tables<'consignors'>, 'id' | 'display_name'>;

type Props = {
  auctionId: string;
  tenantId: string;
  existingLotCount: number;
  onAdded: () => void;
  onCancel: () => void;
};

/**
 * Quick-pick inventory items and add them as lots to an auction.
 *
 * Each selected item becomes a lot with inherited content (title, description,
 * category, consignor) and inherited pricing (starting_bid_cents, reserve,
 * bid_increment_scale_id). Lot numbers increment from the current lot count.
 */
export function AddLotsFromInventory({
  auctionId,
  tenantId,
  existingLotCount,
  onAdded,
  onCancel,
}: Props) {
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [consignors, setConsignors] = useState<Consignor[]>([]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [consignorFilter, setConsignorFilter] = useState('');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 250);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    void (async () => {
      const [{ data: cats }, { data: cons }] = await Promise.all([
        supabase
          .from('item_categories')
          .select('id, name')
          .eq('tenant_id', tenantId)
          .is('archived_at', null)
          .order('name', { ascending: true }),
        supabase
          .from('consignors')
          .select('id, display_name')
          .eq('tenant_id', tenantId)
          .eq('status', 'active')
          .order('display_name', { ascending: true }),
      ]);
      setCategories(cats ?? []);
      setConsignors(cons ?? []);
    })();
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('inventory_items')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .gt('qty_on_hand', 0)
        .order('updated_at', { ascending: false })
        .limit(200);

      if (categoryFilter) query = query.eq('category_id', categoryFilter);
      if (consignorFilter) query = query.eq('consignor_id', consignorFilter);

      const trimmed = search.trim();
      if (trimmed) {
        const escaped = trimmed.replace(/[%_,]/g, (c) => `\\${c}`);
        const pattern = `%${escaped}%`;
        query = query.or(
          [
            `title.ilike.${pattern}`,
            `title_override.ilike.${pattern}`,
            `control_number.ilike.${pattern}`,
            `mpn.ilike.${pattern}`,
            `sku.ilike.${pattern}`,
          ].join(','),
        );
      }

      const { data, error: err } = await query;
      if (cancelled) return;
      if (err) setError(err.message);
      else setItems(data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, search, categoryFilter, consignorFilter]);

  const allVisibleSelected = items.length > 0 && items.every((item) => selected.has(item.id));
  const selectedCount = selected.size;

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const it of items) next.delete(it.id);
      } else {
        for (const it of items) next.add(it.id);
      }
      return next;
    });
  }

  async function handleAdd() {
    if (selected.size === 0) return;
    setSaving(true);
    setError(null);

    const selectedItems = items.filter((it) => selected.has(it.id));
    const lotsToInsert: TablesInsert<'lots'>[] = selectedItems.map((item, idx) => ({
      tenant_id: tenantId,
      auction_id: auctionId,
      inventory_item_id: item.id,
      lot_number: existingLotCount + idx + 1,
      title: item.title_override ?? item.title,
      description: item.description_override ?? item.description,
      condition: item.condition,
      quantity: 1,
      category_id: item.category_id,
      consignor_id: item.consignor_id,
      control_number: item.control_number,
      starting_bid_cents: item.starting_bid_cents ?? 100,
      reserve_cents: item.reserve_cents,
      bid_increment_scale_id: item.bid_increment_scale_id,
      estimate_min_cents: item.estimate_min_cents,
      estimate_max_cents: item.estimate_max_cents,
      status: 'draft',
    }));

    const { error: err } = await supabase.from('lots').insert(lotsToInsert);
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSelected(new Set());
    onAdded();
  }

  const cats = useMemo(() => categories, [categories]);
  const cons = useMemo(() => consignors, [consignors]);

  return (
    <section
      style={{
        border: '1px solid var(--brand-accent)',
        borderRadius: '0.5rem',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
        marginBottom: '1rem',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '0.75rem',
          gap: '0.5rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '0.15rem',
            }}
          >
            Add lots from inventory
          </h3>
          <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', margin: 0 }}>
            Pick items; each becomes a lot with inherited content and pricing.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem' }}>
          <Button type="button" size="sm" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={saving || selectedCount === 0}
            onClick={() => {
              void handleAdd();
            }}
          >
            {saving
              ? 'Adding…'
              : selectedCount === 0
                ? 'Pick items first'
                : `Add ${selectedCount} to this auction`}
          </Button>
        </div>
      </header>

      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          marginBottom: '0.75rem',
          alignItems: 'center',
        }}
      >
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search title, control #, MPN, SKU…"
          style={{
            flex: '1 1 16rem',
            padding: '0.4rem 0.65rem',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
            background: 'var(--surface)',
            color: 'var(--text)',
            fontSize: '0.85rem',
          }}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={selectStyle}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {cats.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={consignorFilter}
          onChange={(e) => setConsignorFilter(e.target.value)}
          style={selectStyle}
          aria-label="Filter by consignor"
        >
          <option value="">All consignors</option>
          {cons.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: '0.5rem 0.75rem',
            borderRadius: '0.375rem',
            background: 'var(--surface-danger, #fef2f2)',
            color: 'var(--text-danger, #991b1b)',
            border: '1px solid var(--border-danger, #fecaca)',
            fontSize: '0.85rem',
            marginBottom: '0.5rem',
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          maxHeight: '28rem',
          overflow: 'auto',
          border: '1px solid var(--border)',
          borderRadius: '0.375rem',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead
            style={{
              position: 'sticky',
              top: 0,
              background: 'var(--surface-muted)',
              zIndex: 1,
            }}
          >
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ ...thStyle, width: '2rem' }}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible"
                />
              </th>
              <th style={thStyle}>Control #</th>
              <th style={thStyle}>Title</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Starting bid</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ ...tdStyle, color: 'var(--text-muted)', textAlign: 'center' }}
                >
                  Loading inventory…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ ...tdStyle, color: 'var(--text-muted)', textAlign: 'center' }}
                >
                  No matching inventory items. Check your filters or add items first.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: selected.has(item.id) ? 'var(--surface-muted)' : undefined,
                    cursor: 'pointer',
                  }}
                  onClick={() => toggleOne(item.id)}
                >
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={() => toggleOne(item.id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`Select ${item.title ?? item.id}`}
                    />
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'var(--font-mono, monospace)' }}>
                    {item.control_number ?? '—'}
                  </td>
                  <td style={tdStyle}>{item.title_override ?? item.title ?? '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{item.qty_on_hand}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {item.starting_bid_cents === null ? '—' : formatCents(item.starting_bid_cents)}
                  </td>
                  <td style={tdStyle}>
                    {item.is_visible ? (
                      <Badge variant="success">Visible</Badge>
                    ) : (
                      <Badge variant="outline">Hidden</Badge>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
        Showing up to 200 most-recently-updated items that have qty on hand. Add items via the
        Inventory page if you don&apos;t see them here.
      </p>
    </section>
  );
}

const thStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.72rem',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  color: 'var(--text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.45rem 0.6rem',
  verticalAlign: 'middle',
};

const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  border: '1px solid var(--border)',
  borderRadius: '0.375rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
  minWidth: '11rem',
};
